import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Calculator, 
  Settings, 
  TrendingUp, 
  MapPin,
  Clock,
  Plus,
  Edit,
  Eye
} from 'lucide-react';
import { ResponsiveTable, StatusBadge } from '../../components/ui/table';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Filter } from '../../components/ui/Filter';
import { Pagination } from '../../components/ui/Pagination';
import { calculateFareEstimate, updateRateCard } from '../../store/fareSlice';

const PAGE_SIZE = 10;

export function FareManagementPage() {
  const dispatch = useDispatch();
  const fareConfig = useSelector(state => state.fares.configuration);
  const fareHistory = useSelector(state => state.fares.calculationHistory);
  const currentEstimate = useSelector(state => state.fares.currentEstimate);
  
  const [activeTab, setActiveTab] = useState('rate-cards');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorData, setCalculatorData] = useState({
    rideType: 'standard',
    distance: '',
    duration: '',
    pickupLocation: ''
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleCalculateFare = () => {
    if (calculatorData.distance && calculatorData.duration) {
      dispatch(calculateFareEstimate({
        rideType: calculatorData.rideType,
        distance: parseFloat(calculatorData.distance),
        estimatedDuration: parseFloat(calculatorData.duration),
        pickupLocation: calculatorData.pickupLocation,
        currentTime: new Date().toISOString()
      }));
    }
  };

  const rateCardColumns = [
    {
      key: 'name',
      header: 'Rate Card',
      primary: true,
      render: (value, item) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{item.id}</div>
        </div>
      )
    },
    {
      key: 'baseFare',
      header: 'Base Fare',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'distanceRate',
      header: 'Distance Rate',
      render: (value) => `${formatCurrency(value)}/km`
    },
    {
      key: 'timeRate',
      header: 'Time Rate',
      render: (value) => `${formatCurrency(value)}/min`
    },
    {
      key: 'minimumFare',
      header: 'Min Fare',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'active',
      header: 'Status',
      render: (value) => (
        <StatusBadge 
          status={value ? 'Active' : 'Inactive'} 
          variant={value ? 'success' : 'default'} 
        />
      )
    }
  ];

  const historyColumns = [
    {
      key: 'calculatedAt',
      header: 'Date',
      primary: true,
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      key: 'rideType',
      header: 'Ride Type',
      render: (value) => (
        <span className="capitalize font-medium">{value.replace('_', ' ')}</span>
      )
    },
    {
      key: 'distance',
      header: 'Distance',
      render: (value) => `${value} km`
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (value) => `${value} min`
    },
    {
      key: 'surge',
      header: 'Surge',
      render: (value) => `${value}x`
    },
    {
      key: 'totalFare',
      header: 'Total Fare',
      render: (value) => formatCurrency(value)
    }
  ];

  const rateCardActions = [
    {
      type: 'button',
      onClick: (item) => console.log('Edit rate card:', item),
      label: 'Edit',
      variant: 'primary'
    },
    {
      type: 'button',
      onClick: (item) => console.log('View details:', item),
      label: 'Details',
      variant: 'secondary'
    }
  ];

  const rateCards = Object.entries(fareConfig.rateCards || {}).map(([key, card]) => ({
    ...card,
    key
  }));

  const filteredHistory = fareHistory.filter(item =>
    item.rideType.toLowerCase().includes(filter.toLowerCase()) ||
    item.tripId.toLowerCase().includes(filter.toLowerCase())
  );

  const totalPages = Math.ceil(filteredHistory.length / PAGE_SIZE);
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = filteredHistory.slice(start, start + PAGE_SIZE);

  const tabs = [
    { id: 'rate-cards', label: 'Rate Cards', icon: Settings },
    { id: 'calculator', label: 'Fare Calculator', icon: Calculator },
    { id: 'history', label: 'Calculation History', icon: Clock },
    // { id: 'surge', label: 'Surge Pricing', icon: TrendingUp }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Fare Management</h1>
          <p className="mt-2 text-gray-600">
            Configure pricing, surge rates, and fare calculations
          </p>
        </div>
        <Button variant="primary" className="mt-4 sm:mt-0">
          <Plus size={16} className="mr-2" />
          Add Rate Card
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon size={16} className="mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'rate-cards' && (
        <div className="space-y-6">
          <ResponsiveTable
            data={rateCards}
            columns={rateCardColumns}
            actions={rateCardActions}
          />
        </div>
      )}

      {activeTab === 'calculator' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Fare Calculator</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Select
                label="Ride Type"
                value={calculatorData.rideType}
                onChange={(e) => setCalculatorData({...calculatorData, rideType: e.target.value})}
                options={['standard', 'premium', 'shared']}
              />
              <Input
                label="Distance (km)"
                type="number"
                value={calculatorData.distance}
                onChange={(e) => setCalculatorData({...calculatorData, distance: e.target.value})}
                placeholder="Enter distance"
              />
              <Input
                label="Duration (minutes)"
                type="number"
                value={calculatorData.duration}
                onChange={(e) => setCalculatorData({...calculatorData, duration: e.target.value})}
                placeholder="Enter duration"
              />
              <Input
                label="Pickup Location"
                value={calculatorData.pickupLocation}
                onChange={(e) => setCalculatorData({...calculatorData, pickupLocation: e.target.value})}
                placeholder="Enter location"
              />
            </div>
            <Button onClick={handleCalculateFare} variant="primary">
              <Calculator size={16} className="mr-2" />
              Calculate Fare
            </Button>

            {currentEstimate && !currentEstimate.error && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Fare Breakdown</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Base Fare:</span>
                    <div className="font-medium">{formatCurrency(currentEstimate.breakdown.baseFare)}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Distance:</span>
                    <div className="font-medium">{formatCurrency(currentEstimate.breakdown.distanceFare)}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Time:</span>
                    <div className="font-medium">{formatCurrency(currentEstimate.breakdown.timeFare)}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Surge:</span>
                    <div className="font-medium">{currentEstimate.breakdown.surgeMultiplier}x</div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Total Fare:</span>
                    <span className="text-2xl font-bold text-green-600">
                      {formatCurrency(currentEstimate.breakdown.totalFare)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1 max-w-md">
              <Filter
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setPage(1);
                }}
                placeholder="Search by ride type or trip ID..."
              />
            </div>
          </div>

          <ResponsiveTable
            data={pageItems}
            columns={historyColumns}
          />

          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          )}
        </div>
      )}

      {/* {activeTab === 'surge' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Surge Pricing Zones</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {fareConfig.surgeZones?.map((zone) => (
                <div key={zone.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">{zone.name}</h3>
                    <StatusBadge 
                      status={zone.active ? 'Active' : 'Inactive'} 
                      variant={zone.active ? 'success' : 'default'} 
                    />
                  </div>
                  <div className="text-sm text-gray-600 mb-3">
                    Current Surge: <span className="font-medium text-orange-600">
                      {zone.currentSurge.multiplier}x ({zone.currentSurge.label})
                    </span>
                  </div>
                  <div className="space-y-2">
                    {zone.peakHours?.map((peak, index) => (
                      <div key={index} className="text-xs text-gray-500">
                        {peak.start} - {peak.end}: {peak.surge.multiplier}x
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
}
